/**
 * 2026 workshop interest questionnaire — edit field ids/options here;
 * backend stores arbitrary key/value pairs. Labels resolve via i18n catalogs.
 */

import { resolveMessage, t } from '../i18n/translate';

export type SurveyFieldType = 'select' | 'radio';

export interface SurveyOption {
  value: string;
  label: string;
}

export interface SurveyField {
  id: string;
  label: string;
  type: SurveyFieldType;
  options: SurveyOption[];
  helper?: string;
  /** Show this field only when another field matches a condition */
  showWhen?: {
    field: string;
    equals?: string;
    in?: string[];
  };
}

interface SurveyOptionDef {
  value: string;
  labelKey: string;
}

interface SurveyFieldDef {
  id: string;
  labelKey: string;
  type: SurveyFieldType;
  options: SurveyOptionDef[];
  helperKey?: string;
  showWhen?: SurveyField['showWhen'];
}

const SURVEY_DEFS: SurveyFieldDef[] = [
  {
    id: 'saa_tenure',
    labelKey: 'workshop.2026.interest_survey.saa_tenure.label',
    type: 'select',
    options: [
      { value: 'new', labelKey: 'workshop.2026.interest_survey.saa_tenure.new' },
      { value: 'lt_1_year', labelKey: 'workshop.2026.interest_survey.saa_tenure.lt_1_year' },
      { value: '1_3_years', labelKey: 'workshop.2026.interest_survey.saa_tenure.1_3_years' },
      { value: '3_10_years', labelKey: 'workshop.2026.interest_survey.saa_tenure.3_10_years' },
      { value: '10_plus_years', labelKey: 'workshop.2026.interest_survey.saa_tenure.10_plus_years' },
    ],
  },
  {
    id: 'steps_worked',
    labelKey: 'workshop.2026.interest_survey.steps_worked.label',
    type: 'radio',
    options: [
      { value: 'none', labelKey: 'workshop.2026.interest_survey.steps_worked.none' },
      { value: 'some', labelKey: 'workshop.2026.interest_survey.steps_worked.some' },
      { value: 'all', labelKey: 'workshop.2026.interest_survey.steps_worked.all' },
    ],
  },
  {
    id: 'workshop_experience',
    labelKey: 'workshop.2026.interest_survey.workshop_experience.label',
    type: 'radio',
    options: [
      { value: 'no', labelKey: 'workshop.2026.interest_survey.workshop_experience.no' },
      { value: 'yes', labelKey: 'workshop.2026.interest_survey.workshop_experience.yes' },
    ],
    showWhen: { field: 'steps_worked', in: ['some', 'all'] },
  },
  {
    id: 'last_step_completed',
    labelKey: 'workshop.2026.interest_survey.last_step_completed.label',
    type: 'select',
    options: Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1),
      labelKey: 'workshop.2026.interest_survey.last_step_completed.option',
    })),
    showWhen: { field: 'steps_worked', equals: 'some' },
  },
  {
    id: 'has_sponsor',
    labelKey: 'workshop.2026.interest_survey.has_sponsor.label',
    type: 'radio',
    options: [
      { value: 'yes', labelKey: 'workshop.2026.interest_survey.has_sponsor.yes' },
      { value: 'no', labelKey: 'workshop.2026.interest_survey.has_sponsor.no' },
      { value: 'looking', labelKey: 'workshop.2026.interest_survey.has_sponsor.looking' },
    ],
    helperKey: 'workshop.2026.interest_survey.has_sponsor.helper',
    showWhen: undefined,
  },
  {
    id: 'help_facilitate',
    labelKey: 'workshop.2026.interest_survey.help_facilitate.label',
    type: 'radio',
    options: [
      { value: 'yes', labelKey: 'workshop.2026.interest_survey.help_facilitate.yes' },
      { value: 'maybe', labelKey: 'workshop.2026.interest_survey.help_facilitate.maybe' },
      { value: 'no', labelKey: 'workshop.2026.interest_survey.help_facilitate.no' },
    ],
    showWhen: { field: 'steps_worked', equals: 'all' },
  },
];

function resolveSurvey(locale?: string): SurveyField[] {
  return SURVEY_DEFS.map((def) => ({
    id: def.id,
    type: def.type,
    showWhen: def.showWhen,
    label: resolveMessage(def.labelKey, { locale }),
    helper: def.helperKey ? resolveMessage(def.helperKey, { locale }) : undefined,
    options: def.options.map((o) => ({
      value: o.value,
      label:
        def.id === 'last_step_completed'
          ? resolveMessage(o.labelKey, { locale, vars: { n: o.value } })
          : resolveMessage(o.labelKey, { locale }),
    })),
  }));
}

/** Localized survey for member UI (active locale). */
export function getWorkshopInterestSurvey(): SurveyField[] {
  return resolveSurvey();
}

/** English survey — admin UIs stay English. */
export const WORKSHOP_INTEREST_SURVEY: SurveyField[] = resolveSurvey('en');

export type InterestSurveyValues = Record<string, string>;

export function emptySurveyValues(): InterestSurveyValues {
  const values: InterestSurveyValues = {};
  for (const field of SURVEY_DEFS) {
    values[field.id] = '';
  }
  return values;
}

export function isFieldVisible(field: SurveyField, values: InterestSurveyValues): boolean {
  if (!field.showWhen) return true;
  const actual = values[field.showWhen.field] || '';
  if (field.showWhen.equals !== undefined) {
    return actual === field.showWhen.equals;
  }
  if (field.showWhen.in !== undefined) {
    return field.showWhen.in.includes(actual);
  }
  return true;
}

export function visibleSurveyFields(
  values: InterestSurveyValues,
  survey: SurveyField[] = getWorkshopInterestSurvey()
): SurveyField[] {
  return survey.filter((f) => isFieldVisible(f, values));
}

/** Clear values for fields that are hidden after a controlling answer changes */
export function pruneHiddenSurveyValues(values: InterestSurveyValues): InterestSurveyValues {
  const next = { ...values };
  const survey = getWorkshopInterestSurvey();
  for (const field of survey) {
    if (!isFieldVisible(field, next)) {
      next[field.id] = '';
    }
  }
  return next;
}

export function validateInterestSurvey(values: InterestSurveyValues): string | null {
  for (const field of visibleSurveyFields(values)) {
    const v = (values[field.id] || '').trim();
    if (!v) {
      return t('workshop.2026.interest_survey.validation_required', {
        question: field.label.replace(/\?$/, ''),
      });
    }
  }
  return null;
}

export function interestSurveyToPayload(values: InterestSurveyValues): {
  interest_responses: InterestSurveyValues;
} {
  const responses: InterestSurveyValues = {};
  for (const field of visibleSurveyFields(values)) {
    responses[field.id] = values[field.id].trim();
  }
  return { interest_responses: responses };
}

export function formatInterestDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function labelForSurveyValue(field: SurveyField, value: string): string {
  const match = field.options.find((o) => o.value === value);
  return match?.label || value;
}

/** One-line summary for admin roster (English). */
export function summarizeInterestResponses(
  responses: Record<string, string> | null | undefined
): string {
  if (!responses || typeof responses !== 'object') return '';
  const survey = resolveSurvey('en');
  const parts: string[] = [];
  for (const field of survey) {
    const raw = (responses[field.id] || '').trim();
    if (!raw) continue;
    if (!isFieldVisible(field, responses)) continue;
    parts.push(labelForSurveyValue(field, raw));
  }
  return parts.join(' · ');
}

/** Full questionnaire answers for expanded admin view (English). */
export function formatInterestResponsesDetail(
  responses: Record<string, string> | null | undefined
): { label: string; value: string }[] {
  if (!responses || typeof responses !== 'object') return [];
  const survey = resolveSurvey('en');
  const rows: { label: string; value: string }[] = [];
  for (const field of survey) {
    const raw = (responses[field.id] || '').trim();
    if (!raw || !isFieldVisible(field, responses)) continue;
    rows.push({ label: field.label, value: labelForSurveyValue(field, raw) });
  }
  return rows;
}

export function collectMemberEmails(
  members: { email?: string; registration_state?: string }[],
  state: 'interest' | 'confirmed'
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of members) {
    if (m.registration_state !== state) continue;
    const email = (m.email || '').trim().toLowerCase();
    if (!email || !email.includes('@') || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out.sort();
}
